import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk';
import { appConfig } from 'config/app';

export interface FeatureFlags {
  connectionsEnabled: boolean;
  jobsitePulseEnabled: boolean;
  procoreSelfServiceEnabled: boolean;
  profileLockingEnabled: boolean;
  showNewJobsiteDashboard: boolean;
  showDroneDemo: boolean;
  workerBetaVersionEnabled: boolean;
  authenticatedSelfOnboardingEnabled: boolean;
  // Global kill switch for the platform policy-consent re-capture gate. Contextless
  // (not jobsite-scoped): a single on/off boolean served regardless of LD context.
  // LD dashboard key: `consent-recapture-gate-enabled`.
  consentRecaptureGateEnabled: boolean;
  // The layout designer and its Devices, Access Groups, and Geofencing tabs in
  // jobsite settings. Jobsite-scoped: read under `JobsiteFeatureFlagsProvider`;
  // off, the Site Layout tab is the geofencing, gateways, and zones tab it was.
  // LD dashboard key: `site-layout-ui-enabled`. Target it on the jobsite
  // context kind only: the API evaluates it per site, without the user, to
  // refuse zones changes, so user targeting would split the app from the API.
  siteLayoutUiEnabled: boolean;
}

/**
 * LaunchDarkly's flags, with one local development override:
 * `LOCAL_SITE_LAYOUT_UI_ENABLED=true` (local, development only) forces
 * `siteLayoutUiEnabled` on, so the layout designer can be previewed without a
 * LaunchDarkly targeting rule for the site. It forces nothing else: every
 * other flag still comes from LaunchDarkly. The override applies to this
 * app's flag reads only.
 */
export const useFlags = (): FeatureFlags => {
  const flags = useLDFlags() as FeatureFlags;
  if (
    appConfig.appEnv === 'local' &&
    appConfig.nodeEnv === 'development' &&
    appConfig.localSiteLayoutUiEnabled
  ) {
    return { ...flags, siteLayoutUiEnabled: true };
  }
  return flags;
};
